import { Grid, useTheme, Stack, CircularProgress, Typography, Card, CardContent, Chip, Divider, Paper, Box, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import { Form, Formik } from 'formik';
import React, { useContext, useState, useEffect, useRef } from 'react';
import FormInput from '../../../SystemDesign/FormInput';
import FormSelect from '../../../SystemDesign/FormSelect';
import MuiDialog from '../../../SystemDesign/MuiDialog';
import * as yup from 'yup';
import { tokens } from '../../../theme';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Button } from '@mui/material';
import { PrimaryButton } from '../../../SystemDesign/Styles';
import { SnackBarContext } from '../../../Context/SnackBarContextProvider';
import { useDispatch } from 'react-redux';
import { ADD_ROLE } from '../../../Reducers/Actions';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import { useCreateRoleMutation, useGetPermissionSetsQuery } from '../../../services/SkillsManagement';
import { translatePermissionSetName, translateModuleName, translatePermissionAction, formatActionsAsSentence } from '../../../Utils/PermissionDictionary';

// Component to display permission set details
const PermissionSetCard = ({ permissionSet }) => {
    if (!permissionSet) return null;

    // Define model categories and their members
    const modelCategories = {
        "Users and Access": [
            "User", "UserProfile", "Role", "Permission", "RolePermission", "UserPermission"
        ],
        "Company Structure": [
            "Company", "Poste"
        ],
        "Recruitment": [
            "Candidate", "Application", "ApplicationNote", "JobOffer", "JobPreferences", "Certificate", "CertificationType"
        ],
        "Training": [
            "Course", "CourseSection", "CourseSession", "CourseCategory", "CourseCategoryMapping", "SessionBooking", "SessionInstructor", "Enrollment", "Instructor"
        ],
        "Competences": [
            "Departement", "Unit", "Skill", "SkillCategoryMapping", "Evaluation"
        ],
        "History and Notifications": [
            "History", "Notification"
        ]
    };

    // Group models into their respective categories
    const categorizedModels = {};
    
    if (permissionSet.modelActions) {
        Object.entries(permissionSet.modelActions).forEach(([model, actions]) => {
            let assigned = false;
            
            // Find which category this model belongs to
            for (const [category, models] of Object.entries(modelCategories)) {
                if (models.includes(model)) {
                    if (!categorizedModels[category]) {
                        categorizedModels[category] = [];
                    }
                    categorizedModels[category].push({ model, actions });
                    assigned = true;
                    break;
                }
            }
            
            // If model doesn't fit any category, add to Others
            if (!assigned) {
                if (!categorizedModels["Autres"]) {
                    categorizedModels["Autres"] = [];
                }
                categorizedModels["Autres"].push({ model, actions });
            }
        });
    }

    return (
        <Accordion 
            sx={{ 
                mb: 1,
                '&:before': {
                    display: 'none',
                },
                boxShadow: 'none',
                border: '1px solid #e0e0e0',
            }}
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ 
                    fontWeight: 'bold',
                    minHeight: '48px',
                    '& .MuiAccordionSummary-content': {
                        margin: '8px 0',
                    }
                }}
            >
                <Typography variant="subtitle1" fontWeight="bold">
                    {translatePermissionSetName(permissionSet.description || permissionSet.name)}
                </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pb: 2 }}>
                <Divider sx={{ mb: 1 }} />
                
                {/* Render each category group */}
                {Object.entries(categorizedModels).map(([category, modelsList]) => (
                    <Box key={category} sx={{ mb: 3 }}>
                        <Typography 
                            variant="subtitle2" 
                            color="primary" 
                            sx={{ 
                                mb: 1,
                                fontWeight: 'bold',
                                backgroundColor: 'rgba(0, 0, 0, 0.03)',
                                p: 1,
                                borderRadius: 1
                            }}
                        >
                            {category}
                        </Typography>
                        
                        {modelsList.map(({ model, actions }) => (
                            <Box key={model} sx={{ mb: 1, ml: 2 }}>
                                <Typography variant="body2">
                                    {formatActionsAsSentence(model, actions)}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                ))}
                
                {/* Display a message if no permissions are found */}
                {Object.keys(categorizedModels).length === 0 && (
                    <Typography variant="body2" sx={{ fontStyle: 'italic', mt: 1 }}>
                        Aucune permission détaillée n'est disponible pour cet ensemble.
                    </Typography>
                )}
            </AccordionDetails>
        </Accordion>
    );
};

const AddRole = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [createRole] = useCreateRoleMutation();
    const { data: permissionSetsData, isLoading: permissionSetsLoading } = useGetPermissionSetsQuery({});
    const { setInfoAlert, setOpenSnackBar } = useContext(SnackBarContext);
    const [close, setClose] = useState(false);
    const dispatch = useDispatch();
    const [permissionSetsOptions, setPermissionSetsOptions] = useState([]);
    const [selectedPermissionSets, setSelectedPermissionSets] = useState([]);
    const formikRef = useRef(null);

    const initialValues = {
        name: '',
        type: '',
        permissionSets: []
    };

    const [formValues, setFormValues] = useState(initialValues);

    useEffect(() => {
        if (permissionSetsData && permissionSetsData.permissionSets) {
            const mappedSets = permissionSetsData.permissionSets.map(set => ({
                id: set.name,
                title: translatePermissionSetName(set.description || set.name)
            }));
            setPermissionSetsOptions(mappedSets);
        }
    }, [permissionSetsData]);

    // Effect to get detailed permission set data when selections change
    useEffect(() => {
        if (permissionSetsData && permissionSetsData.permissionSets) {
            const selectedSets = permissionSetsData.permissionSets.filter(
                set => formValues.permissionSets.includes(set.name)
            );
            setSelectedPermissionSets(selectedSets);
        }
    }, [formValues.permissionSets, permissionSetsData]);

    // Reset form values when dialog closes
    useEffect(() => {
        if (close) {
            setFormValues(initialValues);
            setSelectedPermissionSets([]);
            setClose(false);
        }
    }, [close]);

    const formFields = [
        { type: 'text', name: 'name', label: 'Nom du rôle', required: true },
        { type: 'text', name: 'type', label: 'Type du rôle (MAJUSCULES)', required: true },
        { 
            type: 'select', 
            name: 'permissionSets', 
            label: 'Ensembles de permissions', 
            required: true, 
            options: permissionSetsOptions,
            multiple: true,
            disabled: permissionSetsLoading
        }
    ];

    const formValidationSchema = yup.object().shape({
        name: yup.string()
            .required("Le nom du rôle est requis")
            .min(3, "Le nom du rôle doit contenir au moins 3 caractères"),
        type: yup.string()
            .required("Le type du rôle est requis")
            .max(50, "Le type du rôle est trop long")
            .matches(/^[A-Z_]+$/, 'Le type doit contenir uniquement des lettres majuscules et underscores (ex: ADMIN, SUPERADMIN)'),
        permissionSets: yup.array()
            .min(1, "Veuillez sélectionner au moins un ensemble de permissions")
    });

    const handleSubmit = async (values, actions) => {
        try {
            const response = await createRole({ 
                body: {
                    name: values.name,
                    type: values.type,
                    permissionSets: values.permissionSets
                } 
            }).unwrap();
            dispatch({ type: ADD_ROLE, payload: response.role.role });
            actions.resetForm();
            setFormValues(initialValues); // Reset form values in state
            setSelectedPermissionSets([]);
            setInfoAlert({ severity: 'success', message: `Rôle ${values.name} créé avec succès` });
            setOpenSnackBar(true);
            setClose(true);
        } catch (error) {
            setInfoAlert({ severity: 'error', message: error.data?.message || 'Une erreur s\'est produite lors de la création du rôle' });
            setOpenSnackBar(true);
        }
        actions.setSubmitting(false);
    };

    return (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: "2rem" }}>
            <MuiDialog
                icon={<VerifiedUserIcon />}
                triggerButton={
                    <Button 
                         variant="contained"
            color="primary"
                        endIcon={<AddIcon />}
                    >
                        Rôle
                    </Button>
                }
                maxWidth="md"
                title="Nouveau rôle"
                subtitle="Créer un nouveau rôle"

                content={
                    permissionSetsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Formik
                            initialValues={formValues}
                            validationSchema={formValidationSchema}
                            validateOnChange={false}
                            validateOnBlur={false}
                            enableReinitialize={true}
                            onSubmit={handleSubmit}
                            ref={formikRef}
                        >
                            {(formik) => (
                                <Form id="createRoleForm">
                                    <Grid container spacing={2} marginTop={2}>
                                        {formFields.map((field, index) => (
                                            <Grid item size={{ xs: 12 }} key={index}>
                                                {field.type !== 'select' ? (
                                                    <FormInput
                                                        type={field.type}
                                                        name={field.name}
                                                        label={field.label}
                                                        required={field.required}
                                                        value={formik.values[field.name]}
                                                        onChange={(e) => {
                                                            const raw = e.target.value;
                                                            const value = field.name === 'type'
                                                                ? raw.toUpperCase().replace(/\s+/g, '_')
                                                                : raw;
                                                            formik.setFieldValue(field.name, value, false);
                                                            setFormValues(currentValues => ({
                                                                ...currentValues,
                                                                [field.name]: value
                                                            }));
                                                        }}
                                                    />
                                                ) : (
                                                    <FormSelect
                                                        name={field.name}
                                                        label={field.label}
                                                        options={field.options || []}
                                                        required={field.required}
                                                        multiple={field.multiple}
                                                        value={formik.values[field.name]}
                                                        onChange={(e) => {
                                                            formik.handleChange(e);
                                                            setFormValues(currentValues => ({
                                                                ...currentValues,
                                                                [field.name]: e.target.value
                                                            }));
                                                        }}
                                                        error={formik.touched[field.name] && Boolean(formik.errors[field.name])}
                                                        helperText={formik.touched[field.name] && formik.errors[field.name]}
                                                        disabled={field.disabled}
                                                    />
                                                )}
                                            </Grid>
                                        ))}
                                        
                                        {/* Display permission sets details when selected */}
                                        {selectedPermissionSets.length > 0 && (
                                            <Grid item xs={12}>
                                                <Divider sx={{ mt: 3, mb: 2 }}>
                                                    <Typography variant="body2" color="text.secondary" sx={{ px: 1, fontWeight: 'medium' }}>
                                                        Détails des permissions sélectionnées
                                                    </Typography>
                                                </Divider>
                                                <Box sx={{ mt: 2 }}>
                                                    {selectedPermissionSets.map(permissionSet => (
                                                        <PermissionSetCard 
                                                            key={permissionSet.name} 
                                                            permissionSet={permissionSet} 
                                                        />
                                                    ))}
                                                </Box>
                                            </Grid>
                                        )}
                                    </Grid>
                                </Form>
                            )}
                        </Formik>
                    )
                }
                existAction={true}
                actionTitle="Créer"
                actionFunction={() => {
                    const formikInstance = document.querySelector('form');
                    if (formikInstance) {
                        formikInstance.requestSubmit();
                    }
                }}
                onClose={close}
            />
        </div>
    );
};

export default AddRole;
